import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { ContaTablet, ProdutoCatalogo } from '../api'
import { idLocal } from '../lib/id'
import { precoPorQuantidade } from '../lib/preco-por-quantidade'
import type { FaixaPrecoProduto } from '../api'
import type { AlteracaoPreco, CorrecaoLinhaTotem } from '../lib/reconciliar-carrinho'

const arredondar = (valor: number): number => Number(valor.toFixed(2))

export type PedidoEmAberto = ContaTablet['pedidos'][number]

export interface CartPassoItem {
  idTabelaPrecoItem: number
  idProduto: number
  nome: string
  quantidade: number
  valor: number
}

export interface CartPasso {
  idProdutoPasso: number
  descricao: string | null
  valorPasso: number
  itens: CartPassoItem[]
}

export interface CartItem {
  id: string
  idProduto: number
  nome: string
  quantidade: number
  valorUnitario: number
  valorTotal: number
  temPassos: boolean
  passos: CartPasso[]
  faixasPreco?: FaixaPrecoProduto[]
  valorUnitarioBase?: number
  observacao?: string
}

function reprecificarLinha(linha: CartItem, quantidade: number): void {
  const base = linha.valorUnitarioBase ?? linha.valorUnitario
  linha.quantidade = quantidade
  linha.valorUnitario = precoPorQuantidade(linha.faixasPreco, quantidade, base)
  linha.valorTotal = arredondar(quantidade * linha.valorUnitario)
}

export type KioskView =
  | 'VIEW_START_SCREEN'
  | 'VIEW_IDENTIFIER'
  | 'VIEW_MULTIPLE_ORDERS'
  | 'VIEW_ITEMS'
  | 'VIEW_ADD_ITEMS'
  | 'VIEW_PAYMENT'

export type ModoPagamento = 'CONTA_INTEIRA' | 'ESCOLHER_VALOR'

export interface SelfServiceKioskState {
  currentView: KioskView
  order: PedidoEmAberto | null
  multipleOrders: PedidoEmAberto[]
  cartItems: CartItem[]
  alteracoesPreco: AlteracaoPreco[]
  chaveEnvioItens: string | null
  logoUrl: string | null
  modeloIdentificador: 'MESA' | 'COMANDA'
  identificadorBuscado: string
  stepDialogProduct: ProdutoCatalogo | null
  showReviewItems: boolean
  hasMultipleOrders: boolean
  modoPagamento: ModoPagamento
  emiteNfce: boolean
}

function estadoInicial(): SelfServiceKioskState {
  return {
    currentView: 'VIEW_START_SCREEN',
    order: null,
    multipleOrders: [],
    cartItems: [],
    alteracoesPreco: [],
    chaveEnvioItens: null,
    logoUrl: null,
    modeloIdentificador: 'COMANDA',
    identificadorBuscado: '',
    stepDialogProduct: null,
    showReviewItems: false,
    hasMultipleOrders: false,
    modoPagamento: 'CONTA_INTEIRA',
    emiteNfce: false,
  }
}

const selfServiceKioskSlice = createSlice({
  name: 'selfServiceKiosk',
  initialState: estadoInicial(),
  reducers: {
    atualizarKiosk(state, action: PayloadAction<Partial<SelfServiceKioskState>>) {
      Object.assign(state, action.payload)
    },
    resetarKiosk(state) {
      return {
        ...estadoInicial(),
        logoUrl: state.logoUrl,
        modeloIdentificador: state.modeloIdentificador,
      }
    },

    adicionarProdutoSimples: {
      reducer(
        state,
        action: PayloadAction<{
          id: string
          idProduto: number
          nome: string
          valorUnitario: number
          faixasPreco?: FaixaPrecoProduto[]
        }>,
      ) {
        const { id, idProduto, nome, valorUnitario, faixasPreco } = action.payload
        const linha = state.cartItems.find((c) => c.idProduto === idProduto && !c.temPassos)

        if (linha) {
          reprecificarLinha(linha, linha.quantidade + 1)
          return
        }

        state.cartItems.push({
          id,
          idProduto,
          nome,
          quantidade: 1,
          valorUnitario,
          valorTotal: valorUnitario,
          temPassos: false,
          passos: [],
          faixasPreco,
          valorUnitarioBase: valorUnitario,
        })
      },
      prepare(produto: {
        idProduto: number
        nome: string
        valorUnitario: number
        faixasPreco?: FaixaPrecoProduto[]
      }) {
        return { payload: { ...produto, id: idLocal() } }
      },
    },

    // Do detalhe do produto: mesma observação soma na linha; outra observação é outro pedido para a cozinha.
    adicionarProdutoComDetalhes: {
      reducer(
        state,
        action: PayloadAction<{
          id: string
          idProduto: number
          nome: string
          valorUnitario: number
          faixasPreco?: FaixaPrecoProduto[]
          quantidade: number
          observacao: string
        }>,
      ) {
        const { id, idProduto, nome, valorUnitario, faixasPreco, quantidade, observacao } = action.payload
        const linha = state.cartItems.find((c) => c.idProduto === idProduto && !c.temPassos && (c.observacao ?? '') === observacao)

        if (linha) {
          reprecificarLinha(linha, linha.quantidade + quantidade)
          return
        }

        const nova: CartItem = {
          id,
          idProduto,
          nome,
          quantidade,
          valorUnitario,
          valorTotal: valorUnitario,
          temPassos: false,
          passos: [],
          faixasPreco,
          valorUnitarioBase: valorUnitario,
          ...(observacao ? { observacao } : {}),
        }
        reprecificarLinha(nova, quantidade)
        state.cartItems.push(nova)
      },
      prepare(produto: {
        idProduto: number
        nome: string
        valorUnitario: number
        faixasPreco?: FaixaPrecoProduto[]
        quantidade: number
        observacao: string
      }) {
        return { payload: { ...produto, id: idLocal() } }
      },
    },

    removerProdutoSimples(state, action: PayloadAction<number>) {
      const indice = state.cartItems.findIndex((c) => c.idProduto === action.payload && !c.temPassos)
      const linha = state.cartItems[indice]
      if (!linha) return

      if (linha.quantidade <= 1) {
        state.cartItems.splice(indice, 1)
        return
      }

      reprecificarLinha(linha, linha.quantidade - 1)
    },

    reprecificarItensKiosk(state, action: PayloadAction<CorrecaoLinhaTotem[]>) {
      for (const correcao of action.payload) {
        const linha = state.cartItems.find((c) => c.id === correcao.id)
        if (!linha) continue
        linha.valorUnitario = correcao.valorUnitario
        linha.valorUnitarioBase = correcao.valorUnitarioBase
        linha.valorTotal = correcao.valorTotal
        linha.faixasPreco = correcao.faixasPreco
      }
    },

    alterarQuantidadeItem(state, action: PayloadAction<{ id: string; delta: number }>) {
      const { id, delta } = action.payload
      const indice = state.cartItems.findIndex((c) => c.id === id)
      const linha = state.cartItems[indice]
      if (!linha) return

      const quantidade = linha.quantidade + delta

      if (quantidade <= 0) {
        state.cartItems.splice(indice, 1)
        return
      }

      reprecificarLinha(linha, quantidade)
    },

    duplicarItemKiosk: {
      reducer(state, action: PayloadAction<{ id: string; novoId: string }>) {
        const original = state.cartItems.find((c) => c.id === action.payload.id)
        if (!original) return

        state.cartItems.push({ ...original, id: action.payload.novoId })
      },
      prepare(id: string) {
        return { payload: { id, novoId: idLocal() } }
      },
    },

    removerItemKiosk(state, action: PayloadAction<string>) {
      state.cartItems = state.cartItems.filter((c) => c.id !== action.payload)
    },

    definirObservacaoItemKiosk(state, action: PayloadAction<{ id: string; observacao: string }>) {
      const linha = state.cartItems.find((c) => c.id === action.payload.id)
      if (!linha) return

      const texto = action.payload.observacao
      if (texto.trim()) linha.observacao = texto
      else delete linha.observacao
    },
  },
})

export const {
  atualizarKiosk,
  resetarKiosk,
  adicionarProdutoSimples,
  adicionarProdutoComDetalhes,
  removerProdutoSimples,
  alterarQuantidadeItem,
  reprecificarItensKiosk,
  duplicarItemKiosk,
  removerItemKiosk,
  definirObservacaoItemKiosk,
} = selfServiceKioskSlice.actions
export const selfServiceKioskReducer = selfServiceKioskSlice.reducer